import { useState, useEffect, useCallback } from 'react';

interface TelegramUser {
  id: number;
  firstName: string;
  lastName?: string;
  username?: string;
  languageCode?: string;
  photoUrl?: string;
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  setHeaderColor: (color: 'bg_color' | 'secondary_bg_color' | string) => void;
  setBackgroundColor: (color: 'bg_color' | 'secondary_bg_color' | string) => void;
  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
  showPopup?: (params: {
    title?: string;
    message: string;
    buttons?: Array<{ id?: string; type?: string; text: string }>;
  }, callback?: (buttonId: string) => void) => void;
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    isVisible: boolean;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    chat?: any;
    start_param?: string;
    auth_date: number;
    hash: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  platform: string;
  version: string;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

const INIT_DATA_WAIT_MS = 5000;

export function useTelegram() {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [hasInitData, setHasInitData] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;

    if (!tg) {
      if (import.meta.env.DEV) {
        setUser({
          id: 123456789,
          firstName: 'Test',
          lastName: 'User',
          username: 'testuser',
        });
        setAuthReady(true);
      }
      setIsReady(true);
      return;
    }

    try {
      tg.ready();
      tg.expand();
    } catch {
      // ignore unsupported methods in older Telegram clients
    }

    setWebApp(tg);

    const syncUser = () => {
      if (tg.initDataUnsafe?.user) {
        setUser(tg.initDataUnsafe.user);
      }
    };

    const syncInitData = () => {
      const initData = tg.initData || '';
      if (initData) {
        setHasInitData(true);
        setAuthReady(true);
        syncUser();
        return true;
      }
      return false;
    };

    syncUser();
    if (!syncInitData()) {
      const startedAt = Date.now();
      const timer = window.setInterval(() => {
        if (syncInitData() || Date.now() - startedAt >= INIT_DATA_WAIT_MS) {
          window.clearInterval(timer);
          if (!tg.initData) {
            setHasInitData(false);
            setAuthReady(false);
          }
          setIsReady(true);
        }
      }, 50);
      return () => window.clearInterval(timer);
    }

    setIsReady(true);
  }, []);

  const hapticFeedback = useCallback((type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning') => {
    if (!webApp?.HapticFeedback) return;
    
    // Проверяем версию - HapticFeedback не поддерживается в версии 6.0
    const version = parseFloat(webApp.version || '0');
    if (version < 6.1) return;
    
    try {
      if (['success', 'error', 'warning'].includes(type)) {
        webApp.HapticFeedback.notificationOccurred(type as 'success' | 'error' | 'warning');
      } else {
        webApp.HapticFeedback.impactOccurred(type as 'light' | 'medium' | 'heavy');
      }
    } catch (e) {
      // Игнорируем ошибки, если HapticFeedback не поддерживается
    }
  }, [webApp]);

  const showAlert = useCallback((message: string) => {
    if (webApp) {
      // Проверяем версию - showAlert может не поддерживаться в версии 6.0
      const version = parseFloat(webApp.version || '0');
      if (version >= 6.1) {
        try {
          webApp.showAlert(message);
          return;
        } catch (e) {
          // Игнорируем ошибки
        }
      }
    }
    // Fallback на стандартный alert
    alert(message);
  }, [webApp]);

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (webApp) {
        // Проверяем версию - showConfirm может не поддерживаться в версии 6.0
        const version = parseFloat(webApp.version || '0');
        if (version >= 6.1) {
          try {
            webApp.showConfirm(message, resolve);
            return;
          } catch (e) {
            // Игнорируем ошибки
          }
        }
      }
      // Fallback на стандартный confirm
      resolve(confirm(message));
    });
  }, [webApp]);

  return {
    webApp,
    user,
    isReady,
    hasInitData,
    authReady,
    isTelegramWebApp: !!webApp,
    hapticFeedback,
    showAlert,
    showConfirm,
    colorScheme: webApp?.colorScheme || 'light',
    platform: webApp?.platform || 'unknown',
  };
}



























